import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown, {
  toSingleFilterValue,
} from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import { ICONS } from "../../../constants/icons"
import { del, get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  DEFAULT_STAFF_SORT_BY,
  DEFAULT_STAFF_SORT_DIRECTION,
  getStaffInitials,
  mapStaffRow,
  mapStaffSummaryKpi,
  resolveStaffRolePillTone,
  STAFF_LIST_WITH,
  STAFF_SORT_OPTIONS,
  STAFF_STATUS_TABS,
} from "./adminStaffData"
import CreateStaffModal from "./CreateStaffModal"

const PAGE_SIZE = 15

const STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
}

const formatDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMMM D, YYYY") : "--"
}

/**
 * Admin Staff Management — /api/v1/admin/staff/*
 */
const AdminStaff = () => {
  const navigate = useNavigate()
  const { can, isReady, isSuperAdmin } = useAdminPermission()
  const canCreate = can("staff", "create")
  const canEdit = can("staff", "edit")
  const canDelete = can("staff", "delete")
  const canViewProfile = can("staff", "view")
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [roleFilter, setRoleFilter] = useState("")
  const [roleOptions, setRoleOptions] = useState([])
  const [sortBy, setSortBy] = useState(DEFAULT_STAFF_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_STAFF_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [kpiCards, setKpiCards] = useState(() => mapStaffSummaryKpi())
  const [isKpiLoading, setIsKpiLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  useEffect(() => {
    document.title = "Staff Management | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    if (searchParams.get("create") !== "1") return
    if (!isReady && !isSuperAdmin) return
    if (canCreate) setCreateModalOpen(true)
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("create")
    setSearchParams(nextParams, { replace: true })
  }, [canCreate, isReady, isSuperAdmin, searchParams, setSearchParams])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, roleFilter, sortBy, sortDirection])

  const refreshSummary = useCallback(async () => {
    try {
      const response = await get(endpoints.getAdminStaffSummary)
      if (response?.data?.success) {
        setKpiCards(mapStaffSummaryKpi(response.data.result || {}))
      }
    } catch {
      // list refresh already succeeded
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchSummary = async () => {
      setIsKpiLoading(true)
      try {
        const response = await get(endpoints.getAdminStaffSummary)
        if (cancelled) return
        if (response?.data?.success) {
          setKpiCards(mapStaffSummaryKpi(response.data.result || {}))
        } else {
          toastApiError(response, "Failed to load staff summary")
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load staff summary")
        }
      } finally {
        if (!cancelled) setIsKpiLoading(false)
      }
    }
    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchRoleOptions = async () => {
      try {
        const response = await get(endpoints.getAdminRoles, {
          params: { page: 1, page_size: 100 },
        })
        if (cancelled || !response?.data?.success) return
        const list = response.data.result?.data || []
        setRoleOptions(
          list.map(role => ({
            value: role.id,
            label: role.name || role.id,
          })),
        )
      } catch {
        // filter options optional
      }
    }
    fetchRoleOptions()
    return () => {
      cancelled = true
    }
  }, [])

  const fetchStaff = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        "with[]": STAFF_LIST_WITH,
      }
      if (debouncedSearch) params.search = debouncedSearch
      if (sortBy) params.sort_by = sortBy
      if (sortDirection) params.sort_direction = sortDirection

      const response = await get(endpoints.getAdminStaff, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        let mapped = (result.data || []).map(mapStaffRow)

        if (activeTab === "active") {
          mapped = mapped.filter(row => row.isActive)
        } else if (activeTab === "inactive") {
          mapped = mapped.filter(row => !row.isActive)
        }

        if (roleFilter) {
          mapped = mapped.filter(row =>
            row.roleIds.some(id => String(id) === String(roleFilter)),
          )
        }

        setRows(mapped)
        setTotalItems(result.total || mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total || mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load staff")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load staff")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [
    activeTab,
    currentPage,
    debouncedSearch,
    roleFilter,
    sortBy,
    sortDirection,
  ])

  useEffect(() => {
    fetchStaff()
  }, [fetchStaff])

  const filterRoleOptions = useMemo(() => {
    const base = [...roleOptions]
    const seen = new Set(base.map(option => option.value))
    rows.forEach(row => {
      row.roleIds.forEach((id, index) => {
        if (id && !seen.has(id)) {
          seen.add(id)
          base.push({
            value: id,
            label: row.roleLabels[index] || id,
          })
        }
      })
    })
    return base
  }, [roleOptions, rows])

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const handleViewProfile = row => {
    navigate(`/admin/staff/${row.id}`)
  }

  const closeDeleteModal = () => {
    if (isDeleting) return
    setItemToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id || isDeleting) return
    setIsDeleting(true)
    try {
      const response = await del(endpoints.deleteAdminStaff(itemToDelete.id), {
        skipErrorToast: true,
      })
      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to delete staff member")
        return
      }
      toast.success(
        response?.data?.message || `“${itemToDelete.name}” deleted.`,
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      setItemToDelete(null)
      await fetchStaff()
      await refreshSummary()
    } catch (err) {
      toastApiError(err, "Failed to delete staff member")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const params = {}
      if (debouncedSearch) params.search = debouncedSearch
      if (sortBy) params.sort_by = sortBy
      if (sortDirection) params.sort_direction = sortDirection

      const response = await get(endpoints.exportAdminStaff, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export staff")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export staff"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `staff-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Staff exported.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    } catch (err) {
      toastApiError(err, "Failed to export staff")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="admin-page admin-staff">
      <div className="admin-staff__kpi-grid">
        {kpiCards.map(card => (
          <DashboardCard
            key={card.key}
            title={card.title}
            tone={card.tone}
            className="admin-staff__kpi-card"
            fullHeight
          >
            <p className="admin-staff__kpi-value">
              {isKpiLoading ? (
                <span className="admin-staff__skeleton admin-staff__skeleton--kpi" />
              ) : (
                card.value
              )}
            </p>
          </DashboardCard>
        ))}
      </div>

      <div className="admin-staff__toolbar">
        <div className="admin-staff__toolbar-left">
          <h1 className="admin-staff__title">Staff Members</h1>
          <ListingTabs
            items={STAFF_STATUS_TABS}
            activeKey={activeTab}
            onChange={setActiveTab}
          />
        </div>
        <div className="admin-staff__controls">
          <SearchInput
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search by name..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-staff__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "role",
                type: "select",
                label: "Role",
                options: filterRoleOptions,
                value: roleFilter,
                defaultValue: "",
                onChange: next => {
                  setRoleFilter(toSingleFilterValue(next))
                },
              },
              {
                key: "sortBy",
                type: "sortBy",
                label: "Sort By",
                value: sortBy,
                defaultValue: DEFAULT_STAFF_SORT_BY,
                onChange: next => {
                  setSortBy(next || DEFAULT_STAFF_SORT_BY)
                },
                options: STAFF_SORT_OPTIONS,
              },
              {
                key: "sortDirection",
                type: "sortDirection",
                label: "Order",
                value: sortDirection,
                defaultValue: DEFAULT_STAFF_SORT_DIRECTION,
                onChange: next => {
                  setSortDirection(next || DEFAULT_STAFF_SORT_DIRECTION)
                },
              },
            ]}
          />
          <ExportLogButton
            entityLabel="staff"
            onClick={handleExport}
            loading={isExporting}
            disabled={isExporting}
          />
          {canCreate ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={() => setCreateModalOpen(true)}
            >
              Add Staff
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-staff__table-wrapper">
        <div
          className="admin-staff__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-staff__table-row admin-staff__table-row--head"
            role="row"
          >
            <div role="columnheader">Name</div>
            <div role="columnheader">Email</div>
            <div role="columnheader">Role</div>
            <div role="columnheader">City</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Joined Date</div>
            <div role="columnheader" className="admin-staff__actions-head">
              Actions
            </div>
          </div>

          {isLoading &&
            skeletonRows.map(key => (
              <div
                key={key}
                className="admin-staff__table-row admin-staff__table-row--skeleton"
                role="row"
              >
                <div className="admin-staff__skeleton" />
                <div className="admin-staff__skeleton" />
                <div className="admin-staff__skeleton" />
                <div className="admin-staff__skeleton" />
                <div className="admin-staff__skeleton" />
                <div className="admin-staff__skeleton" />
                <div className="admin-staff__skeleton" />
              </div>
            ))}

          {!isLoading &&
            rows.map(row => (
              <div key={row.id} className="admin-staff__table-row" role="row">
                <div className="admin-staff__user" role="cell">
                  <div className="admin-staff__avatar" aria-hidden="true">
                    {getStaffInitials(row.name)}
                  </div>
                  <TruncatedText className="admin-staff__name">
                    {row.name}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-staff__cell"
                  role="cell"
                >
                  {row.email}
                </TruncatedText>
                <div className="admin-staff__cell" role="cell">
                  <TruncatedText
                    className={`admin-staff__role-pill admin-staff__role-pill--${resolveStaffRolePillTone(
                      row.role,
                    )}`}
                    title={row.role}
                  >
                    {row.role}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-staff__cell admin-staff__cell--cities"
                  role="cell"
                  title={row.citiesLabel}
                >
                  {row.citiesLabel}
                </TruncatedText>
                <div className="admin-staff__status-cell" role="cell">
                  <span
                    className={`admin-staff__status admin-staff__status--${row.status}`}
                  >
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-staff__cell"
                  role="cell"
                >
                  {formatDate(row.joinedDate)}
                </TruncatedText>
                <div className="admin-staff__actions" role="cell">
                  {canViewProfile ? (
                    <CustomButton
                      variant="transparent"
                      size="sm"
                      type="button"
                      trailingIconSrc={ICONS.arrowRight12}
                      trailingIconAlt=""
                      onClick={() => handleViewProfile(row)}
                    >
                      View Profile
                    </CustomButton>
                  ) : null}
                  {canEdit ? (
                    <CustomIconButton
                      iconSrc={ICONS.edit}
                      label={`Edit ${row.name}`}
                      size="sm"
                      onClick={() => handleViewProfile(row)}
                    />
                  ) : null}
                  {canDelete ? (
                    <CustomIconButton
                      iconSrc={ICONS.trash}
                      label={`Delete ${row.name}`}
                      tone="danger"
                      size="sm"
                      onClick={() => setItemToDelete(row)}
                    />
                  ) : null}
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-staff__empty">
              <EmptyState
                title="No staff found"
                description="Try adjusting your search or filters."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-staff__empty">
              <EmptyState
                title="Unable to load staff"
                description={error}
                actionText="Try again"
                onActionClick={fetchStaff}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Staff"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-staff__pagination"
      />

      <CreateStaffModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={async () => {
          await fetchStaff()
          await refreshSummary()
        }}
      />

      <DeleteConfirmation
        isOpen={Boolean(itemToDelete)}
        onCancel={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete Staff Member"
        message={
          itemToDelete
            ? `Are you sure you want to delete “${itemToDelete.name}”? This action cannot be undone.`
            : ""
        }
        cancelText="Cancel"
        confirmText="Delete Staff"
        confirmLoadingText="Deleting..."
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default AdminStaff
